import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Sparkles, X, ArrowRight } from "lucide-react";
import CompleteProfileDialog from "./CompleteProfileDialog";

/**
 * CompleteProfileBanner
 *
 * Banner giallo persistente in cima alla dashboard cliente che indica
 * quanti strumenti AI sono ancora bloccati per profilo incompleto.
 *
 * Visibile se almeno uno tra { name_salone, positioning } manca. Si
 * nasconde definitivamente (per quella sessione) col bottone X — torna
 * al prossimo login finché il profilo non è completo.
 */
export default function CompleteProfileBanner({ onCompleted }) {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const setup = user?.profile_setup;
  if (!setup) return null;
  if (dismissed) return null;

  // Banner visibile solo se mancano dati "core" (salone o positioning)
  const hasCore = setup.has_salone && setup.has_positioning;
  if (hasCore) return null;

  // Conta quanti strumenti AI sono ancora bloccati
  // CaptionHashtag, Cover, DailyIdea richiedono positioning + salone (3)
  // StoryIdeas richiede in più la city (1)
  let lockedCount = 0;
  if (!setup.has_salone || !setup.has_positioning) lockedCount += 3;
  if (!setup.has_city) lockedCount += 1;

  return (
    <>
      <div
        data-testid="complete-profile-banner"
        className="rounded-3xl border-2 border-[var(--mp-ink)] bg-[var(--mp-yellow)] p-4 sm:p-5 shadow-brutal flex flex-wrap items-center gap-3"
      >
        <div className="h-10 w-10 rounded-full bg-[var(--mp-ink)] grid place-items-center shrink-0">
          <Sparkles className="h-5 w-5 text-[var(--mp-yellow)]" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-display text-lg sm:text-xl tracking-tight">
            Completa il profilo (1 minuto) e sblocchi {lockedCount} strumenti AI
          </div>
          <div className="text-[12.5px] text-[var(--mp-ink)] opacity-80 mt-0.5">
            Idee video personalizzate, caption + hashtag su misura,
            copertine AI con il tuo brand. Senza profilo l&apos;AI lavora
            generico.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          data-testid="complete-profile-banner-cta"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[var(--mp-ink)] hover:bg-black text-white text-sm font-semibold transition-colors"
        >
          Compila ora
          <ArrowRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="complete-profile-banner-dismiss"
          aria-label="Chiudi"
          title="Chiudi (riapparirà al prossimo login)"
          className="h-9 w-9 rounded-full border border-[var(--mp-ink)] grid place-items-center hover:bg-white/30"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <CompleteProfileDialog open={dialogOpen} onOpenChange={setDialogOpen} onCompleted={onCompleted} />
    </>
  );
}
